import { COLORS_FAMILY_KEY } from '@/constants/colors-family-key';
import { FILTER_CODE_TO_PATH_MAP, FILTER_PATH_ORDER, FILTER_PATH_TO_CODE_MAP } from '@/constants/filter-map';

const resolveColorLabels = ({
  colorsFamilyRewrite,
  colorRewrite,
  queryColorsFamilyRewrites,
  queryColorRewrites,
  aggregations,
  colorOptions,
  colorsFamilyLabels,
}: {
  colorsFamilyRewrite: string;
  colorRewrite: string;
  queryColorsFamilyRewrites: string[];
  queryColorRewrites: string[];
  aggregations: any[];
  colorOptions: any[];
  colorsFamilyLabels: string[];
}) => {
  const labels: Set<string> = new Set();
  const colorAggregationValueMapToRewrite = aggregations.reduce<Record<string, string>>((acc, cur) => {
    if (cur?.code !== 'color') {
      return acc;
    }

    const value = cur?.option_id;
    const rewrite = cur?.rewrite;

    if (!value || !rewrite) {
      return acc;
    }

    acc[value] = cur.rewrite;

    return acc;
  }, {});
  const categoryColorRewriteMapToLabel = colorOptions.reduce<Record<string, string>>((acc, cur) => {
    const value = cur?.value;
    const label = cur.label;

    if (!value || !label) {
      return acc;
    }

    const rewrite = colorAggregationValueMapToRewrite[value];

    if (!rewrite) {
      return acc;
    }

    acc[rewrite] = label;

    return acc;
  }, {});
  const colorsFamilyRewriteMapToLabel = colorsFamilyLabels.reduce<Record<string, string>>((acc, cur) => {
    const rewrite = cur?.toLowerCase()?.replace(/\s/g, '-');

    if (!rewrite) {
      return acc;
    }

    acc[rewrite] = cur;

    return acc;
  }, {});

  if (colorsFamilyRewrite) {
    const label = colorsFamilyRewriteMapToLabel[colorsFamilyRewrite];

    label && labels.add(label);
  }

  if (colorRewrite) {
    const label = categoryColorRewriteMapToLabel[colorRewrite];

    label && labels.add(label);
  }

  if (queryColorsFamilyRewrites.length > 0) {
    for (const queryColorsFamilyRewrite of queryColorsFamilyRewrites) {
      const label = colorsFamilyRewriteMapToLabel[queryColorsFamilyRewrite];

      label && labels.add(label);
    }
  }

  if (queryColorRewrites.length > 0) {
    for (const queryColorRewrite of queryColorRewrites) {
      const label = categoryColorRewriteMapToLabel[queryColorRewrite];

      label && labels.add(label);
    }
  }

  return Array.from(labels);
};

export const convertStateToNewFilter = (filterState: FilterState) => {
  return filterState.reduce<Record<string, Record<string, any>>>((acc, cur) => {
    const { attributeCode, options } = cur;
    const oneOption = options.length === 1;

    if (attributeCode === 'price') {
      const [from, to] = options[0].rewrite.split('_');

      const rangeFilter: Record<string, string> = {
        from,
        to,
      };

      if (rangeFilter.from === '*') {
        delete rangeFilter.from;
      }

      if (rangeFilter.to === '*') {
        delete rangeFilter.to;
      }

      acc[attributeCode] = rangeFilter;
    } else {
      acc[attributeCode] = {
        [oneOption ? 'eq' : 'in']: oneOption ? options[0].value : options.map(x => x.value),
      };
    }

    return acc;
  }, {});
};

export const getFilterStateFromPath = ({
  categoryUrlKey,
  aggregations,
  pathname,
  filterOptions,
  queryColorsFamily,
  queryColor,
}: {
  categoryUrlKey: string;
  pathname: string;
  filterOptions: any[];
  aggregations: {
    code: string;
    option_id: string;
    rewrite: string;
    label?: string;
  }[];
  queryColorsFamily?: string;
  queryColor?: string;
}): {
  filterState: { attributeCode: string; options: { label: string; rewrite: string; value: string }[] }[];
  hasInvalidPath: boolean;
} => {
  const { aggregationsRewriteMap, aggregationsOptionIdMap } = aggregations
    ? aggregations.reduce<{
        aggregationsRewriteMap: Record<string, Record<string, { option_id: string; label: string; rewrite: string }>>;
        aggregationsOptionIdMap: Record<string, Record<string, { option_id: string; label: string; rewrite: string }>>;
      }>(
        (acc, cur) => {
          const { code, option_id, rewrite, label } = cur;

          if (!code || !option_id || !rewrite) return acc;

          if (acc.aggregationsOptionIdMap[code]) {
            acc.aggregationsOptionIdMap[code][option_id] = {
              option_id,
              label: label || '',
              rewrite,
            };
          } else {
            acc.aggregationsOptionIdMap[code] = {
              [option_id]: {
                option_id,
                label: label || '',
                rewrite,
              },
            };
          }

          if (acc.aggregationsRewriteMap[code]) {
            acc.aggregationsRewriteMap[code][rewrite] = {
              option_id,
              label: label || '',
              rewrite,
            };
          } else {
            acc.aggregationsRewriteMap[code] = {
              [rewrite]: {
                option_id,
                label: label || '',
                rewrite,
              },
            };
          }

          return acc;
        },
        {
          aggregationsRewriteMap: {},
          aggregationsOptionIdMap: {},
        },
      )
    : {
        aggregationsRewriteMap: {},
        aggregationsOptionIdMap: {},
      };

  const filterState: { attributeCode: string; options: { label: string; rewrite: string; value: string }[] }[] = [];
  const filterStateCodeIndexMap: Record<string, number> = {};
  let hasInvalidPath = false;
  const pathParts = pathname.split('/').filter(Boolean);

  const filteredPathParts = pathParts.filter((subPath, index) => {
    if (!subPath) return false;

    if (index > 0 && pathParts[index - 1] === 'p' && /\d+/.test(subPath)) {
      return false;
    }

    if (subPath === 'p') {
      return false;
    }

    if (subPath === categoryUrlKey) {
      return false;
    }

    return true;
  });

  if (filteredPathParts.length === 1) {
    return {
      filterState,
      hasInvalidPath: true,
    };
  }

  const colorAggregations: any[] = [];
  const colorOptionIdMap: Record<string, true> = {};
  const colorsFamilyRewriteMapToColorAggregations =
    filterOptions
      .find(x => x.attribute_code === COLORS_FAMILY_KEY)
      ?.options?.reduce((acc, cur) => {
        const colorsFamilyRewrite = cur.label?.toLowerCase().replace(/\s/g, '-') || '';

        if (!colorsFamilyRewrite) {
          return acc;
        }

        const colorAggregations = cur.value.split(',').reduce((acc, colorValue) => {
          const aggregation = aggregationsOptionIdMap['color']?.[colorValue];

          if (aggregation) {
            acc.push(aggregation);
          }

          return acc;
        }, []);

        acc[colorsFamilyRewrite] = colorAggregations;

        return acc;
      }, {}) || {};

  for (let i = 0, len = filteredPathParts.length; i < len; i++) {
    if (i === 0) continue;

    const key = filteredPathParts[i - 1];
    const rewrite = filteredPathParts[i];

    if (!key || !rewrite) {
      hasInvalidPath = true;
      continue;
    }

    if (key === COLORS_FAMILY_KEY) {
      const pathColorAggregations = colorsFamilyRewriteMapToColorAggregations?.[rewrite];

      if (pathColorAggregations) {
        for (const pathColorAggregation of pathColorAggregations) {
          if (!colorOptionIdMap[pathColorAggregation.option_id]) {
            colorOptionIdMap[pathColorAggregation.option_id] = true;
            colorAggregations.push(pathColorAggregation);
          }
        }
      } else {
        hasInvalidPath = true;
      }

      i++;
      continue;
    }

    if (key === 'color') {
      const aggregation = aggregationsRewriteMap['color']?.[rewrite];
      const attributeCode = FILTER_PATH_TO_CODE_MAP[key];
      const isValidColor = !!filterOptions
        .find(temp => temp.attribute_code === attributeCode)
        ?.options?.some(x => x.value === aggregation?.option_id);

      if (aggregation && isValidColor) {
        if (!colorOptionIdMap[aggregation.option_id]) {
          colorOptionIdMap[aggregation.option_id] = true;
          colorAggregations.push(aggregation);
        }
      } else {
        hasInvalidPath = true;
      }

      i++;
      continue;
    }

    if (key === 'price') {
      const hasPriceFilter = !!filterOptions.find(temp => temp.attribute_code === FILTER_PATH_TO_CODE_MAP[key]);

      if (hasPriceFilter) {
        if (filterStateCodeIndexMap.price === undefined) {
          filterStateCodeIndexMap.price = filterState.length;
          const priceRewrite = rewrite.replace('-', '_');
          filterState.push({
            attributeCode: 'price',
            options: [
              {
                label: rewrite,
                value: priceRewrite,
                rewrite: priceRewrite,
              },
            ],
          });
        }
      } else {
        hasInvalidPath = true;
      }

      i++;
      continue;
    }

    if (FILTER_PATH_TO_CODE_MAP[key]) {
      let attributeCode = FILTER_PATH_TO_CODE_MAP[key];

      if (key === 'size') {
        attributeCode = 'aw_general_size';
      }

      const aggregation = aggregationsRewriteMap[attributeCode]?.[rewrite];

      if (!aggregation) {
        hasInvalidPath = true;
        i++;
        continue;
      }

      const matchedFilterLabel = filterOptions
        .find(temp => temp.attribute_code === attributeCode)
        ?.options?.find(x => x.value === aggregation.option_id)?.label;

      if (!matchedFilterLabel) {
        hasInvalidPath = true;
        i++;
        continue;
      }

      const prevIndex = filterStateCodeIndexMap[attributeCode];
      if (prevIndex === undefined) {
        filterStateCodeIndexMap[attributeCode] = filterState.length;
        filterState.push({
          attributeCode,
          options: [
            {
              label: matchedFilterLabel,
              rewrite: aggregation.rewrite,
              value: aggregation.option_id,
            },
          ],
        });
      } else {
        filterState[prevIndex].options.push({
          label: matchedFilterLabel,
          rewrite: aggregation.rewrite,
          value: aggregation.option_id,
        });
      }

      i++;
      continue;
    }

    hasInvalidPath = true;
  }

  if (queryColor) {
    const queryColorRewrites = queryColor.split(',');

    for (const queryColorRewrite of queryColorRewrites) {
      const aggregation = aggregationsRewriteMap.color?.[queryColorRewrite];

      if (aggregation && !colorOptionIdMap[aggregation.option_id]) {
        colorOptionIdMap[aggregation.option_id] = true;
        colorAggregations.push(aggregation);
      }
    }
  }

  if (queryColorsFamily) {
    const queryColorsFamilyRewrites = queryColorsFamily.split(',');

    for (const queryColorFamilyRewrite of queryColorsFamilyRewrites) {
      const queryColorRewrites = colorsFamilyRewriteMapToColorAggregations?.[queryColorFamilyRewrite];

      if (queryColorRewrites) {
        for (const aggregation of queryColorRewrites) {
          if (!colorOptionIdMap[aggregation.option_id]) {
            colorOptionIdMap[aggregation.option_id] = true;
            colorAggregations.push(aggregation);
          }
        }
      }
    }
  }

  if (colorAggregations.length !== 0) {
    for (const colorAggregation of colorAggregations) {
      const prevIndex = filterStateCodeIndexMap.custom_option_color_ids;
      if (prevIndex === undefined) {
        filterStateCodeIndexMap.custom_option_color_ids = filterState.length;
        filterState.push({
          attributeCode: 'custom_option_color_ids',
          options: [
            {
              label: colorAggregation.label,
              rewrite: colorAggregation.rewrite,
              value: colorAggregation.option_id,
            },
          ],
        });
      } else {
        filterState[prevIndex].options.push({
          label: colorAggregation.label,
          rewrite: colorAggregation.rewrite,
          value: colorAggregation.option_id,
        });
      }
    }
  }

  return {
    filterState,
    hasInvalidPath,
  };
};

export const resolveCategoryLayerFilterLabels = ({
  colorsFamilyRewrite,
  colorRewrite,
  colorOptions,
  aggregations,
  colorsFamilyOptions,
  filterState,
  queryColorsFamilyRewrites,
  queryColorRewrites,
}: {
  colorRewrite: string;
  colorsFamilyRewrite: string;
  aggregations: any[];
  colorOptions: any[];
  colorsFamilyOptions: any[];
  filterState: FilterState;
  queryColorsFamilyRewrites: string[];
  queryColorRewrites: string[];
}) => {
  const layerFilterLabels: string[] = [];
  const layerFilterItemAttrMap = {
    custom_option_size_ids: 1,
    aw_general_fabric: 1,
    aw_dresses_silhouette: 1,
    aw_dresses_hemline: 1,
    aw_dresses_neckline: 1,
    aw_dresses_backdetail: 1,
    aw_dresses_straps_sleeves: 1,
    aw_general_embellishment: 1,
    aw_heel_height: 1,
    aw_heel_type: 1,
  };
  const colorLabels = resolveColorLabels({
    colorsFamilyRewrite,
    colorRewrite,
    queryColorsFamilyRewrites,
    queryColorRewrites,
    aggregations,
    colorOptions,
    colorsFamilyLabels: colorsFamilyOptions?.map(x => x.label) || [],
  });

  if (colorLabels.length) {
    for (const colorLabel of colorLabels) {
      layerFilterLabels.push(colorLabel);
    }
  }

  const sortedFilterState = filterState.slice(0).sort((a, b) => {
    const filterPathA = FILTER_CODE_TO_PATH_MAP[a.attributeCode];
    const filterPathB = FILTER_CODE_TO_PATH_MAP[b.attributeCode];
    const orderA = filterPathA && filterPathA in FILTER_PATH_ORDER ? FILTER_PATH_ORDER[filterPathA]! : null;
    const orderB = filterPathB && filterPathB in FILTER_PATH_ORDER ? FILTER_PATH_ORDER[filterPathB]! : null;

    if (orderA === orderB) return 0;

    if (orderA === null && orderB !== null) return 1;

    if (orderA !== null && orderB === null) return 0;

    return orderA! - orderB!;
  });

  for (const { attributeCode, options } of sortedFilterState) {
    if (layerFilterItemAttrMap[attributeCode] === 1) {
      for (const option of options) {
        layerFilterLabels.push(option.label);
      }
    }
  }

  return layerFilterLabels;
};
